import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, XCircle, ArrowRight, CreditCard } from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SubscriptionSuccess() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const [paymentData, setPaymentData] = useState(null);

  useEffect(() => {
    const checkPaymentStatus = async () => {
      const sessionId = searchParams.get('session_id');
      
      if (!sessionId) {
        setStatus('error');
        return;
      }

      let attempts = 0;
      const maxAttempts = 5;
      const pollInterval = 2000;

      const poll = async () => {
        if (attempts >= maxAttempts) {
          setStatus('pending');
          return;
        }

        try {
          const response = await fetch(`${API}/payments/status/${sessionId}`, {
            credentials: 'include'
          });

          if (response.ok) {
            const data = await response.json();
            setPaymentData(data);

            if (data.payment_status === 'paid') {
              setStatus('success');
              return;
            } else if (data.status === 'expired') {
              setStatus('expired');
              return;
            }
          }

          attempts++;
          setTimeout(poll, pollInterval);
        } catch (error) {
          console.error('Error checking status:', error);
          attempts++;
          setTimeout(poll, pollInterval);
        }
      };

      poll();
    };

    checkPaymentStatus();
  }, [searchParams]);

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />
      
      <main className="pt-24 pb-16 px-4">
        <div className="max-w-lg mx-auto text-center">
          {status === 'loading' && (
            <div className="py-20">
              <div className="animate-spin w-16 h-16 border-4 border-emerald-700 border-t-transparent rounded-full mx-auto mb-6"></div>
              <h1 className="text-2xl font-bold text-stone-900 mb-2">Verificando pago...</h1>
              <p className="text-stone-500">Por favor espera mientras confirmamos tu suscripción</p>
            </div>
          )}

          {status === 'success' && (
            <div className="py-16">
              <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-emerald-700" />
              </div>
              <h1 className="text-3xl font-bold text-stone-900 mb-4">¡Suscripción Activada!</h1>
              <p className="text-stone-600 mb-8">
                Tu suscripción ha sido activada exitosamente. Ya puedes disfrutar de todos los beneficios.
              </p>
              {paymentData && (
                <div className="bg-white rounded-2xl p-6 border border-stone-100 mb-8 text-left">
                  <p className="text-sm text-stone-500 mb-2">Total pagado</p>
                  <p className="text-3xl font-bold text-stone-900">
                    ${(paymentData.amount_total / 100).toFixed(2)} {paymentData.currency.toUpperCase()}
                  </p>
                </div>
              )}
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/dashboard">
                  <Button className="bg-emerald-700 hover:bg-emerald-800 rounded-full px-8" data-testid="go-to-dashboard-btn">
                    Ver Mi Suscripción
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/menu">
                  <Button variant="outline" className="rounded-full px-8 border-stone-200" data-testid="start-ordering-btn">
                    <CreditCard className="w-4 h-4 mr-2" />
                    Empezar a Ordenar
                  </Button>
                </Link>
              </div>
            </div>
          )}

          {status === 'pending' && (
            <div className="py-16">
              <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <div className="animate-spin w-8 h-8 border-4 border-yellow-600 border-t-transparent rounded-full"></div>
              </div>
              <h1 className="text-2xl font-bold text-stone-900 mb-4">Procesando Suscripción</h1>
              <p className="text-stone-600 mb-8">
                Tu suscripción está siendo procesada. Recibirás una confirmación pronto.
              </p>
              <Link to="/dashboard">
                <Button className="bg-emerald-700 hover:bg-emerald-800 rounded-full px-8">
                  Ir al Dashboard
                </Button>
              </Link>
            </div>
          )}

          {(status === 'error' || status === 'expired') && (
            <div className="py-16">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-10 h-10 text-red-600" />
              </div>
              <h1 className="text-2xl font-bold text-stone-900 mb-4">
                {status === 'expired' ? 'Sesión Expirada' : 'Error en el Pago'}
              </h1>
              <p className="text-stone-600 mb-8">
                {status === 'expired'
                  ? 'Tu sesión de pago ha expirado. Por favor intenta de nuevo.'
                  : 'Hubo un problema con tu pago. Por favor intenta de nuevo.'}
              </p>
              <Link to="/subscriptions">
                <Button className="bg-emerald-700 hover:bg-emerald-800 rounded-full px-8">
                  Ver Planes
                </Button>
              </Link>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
